/**
 * StoryEditor — the story editor (spec 010), with two views of one story
 * (spec 025): the story studio (`StoryStudio`, chat-first, for Maya) and
 * Under the hood (the graph, its tabs and the scene editors). Both share the
 * toolbar and the stores; the switch saves a dirty scene first, carries the
 * selection, and writes the remembered view and the URL's `view` query.
 *
 * Under the hood:
 * - Toolbar: name, save status, publish badge, Device (spec 013), Publish, Save
 * - Left panel: "Scenes | Assistant" tabs (spec 024 D-5): the story's scenes
 *   (owned / referenced), or the story assistant's chat
 * - Center: React Flow graph canvas with a live validation status line
 *
 * The store (`storyStore`) is the source of truth for the graph. Every user
 * change goes through a store action and React Flow re-renders from the
 * store; the local React Flow state only carries what React Flow measures
 * (node sizes) and the current selection.
 */

import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Connection,
  type NodeTypes,
  type Node,
  type Edge,
  Handle,
  Position,
  type NodeChange,
  type EdgeChange,
  type OnSelectionChangeParams,
  type FitViewOptions,
  type OnBeforeDelete,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useStoryStore, availableChoices } from '../stores/storyStore';
import { toReactFlowNodes, toReactFlowEdges } from '../utils/graphAdapter';
import type { StorySceneWithScene, ChoiceOption } from '../../../Shared/src/@types';
import { useModalContext } from '../contexts/ModalContext';
import { apiClient } from '../api/client';
import ScenePicker from '../modals/ScenePicker';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import ShareDialog from '../modals/ShareDialog';
import { SceneEditor } from './SceneEditor';
import { cacheDraft, useSceneStore } from '../stores/sceneStore';
import StoryPreviewPane, { type OrientationMode } from '../components/StoryPreviewPane';
import { useStoryPreviewPopout } from '../hooks/useStoryPreviewPopout';
import { useStoryShortcuts } from '../hooks/useStoryShortcuts';
import StoryAssistantPanel from '../components/assistant/StoryAssistantPanel';
import { useAssistantStore } from '../stores/assistantStore';
import StoryStudio from '../components/studio/StoryStudio';
import { useAssistantView } from '../components/assistant/assistantView';
import StoryDeviceButton from '../components/StoryDeviceButton';
import { useDeviceStorySync } from '../contexts/DevicePreviewContext';
import {
  isStoryEditorView, isStoryView, resolveStoryView, sessionUserId, writeStoredView, type StoryEditorView,
} from '../utils/storyView';
import PicturesView from '../components/pictures/PicturesView';
import { picturesSearch } from '../components/pictures/usePicturesNav';
import { pictureMapCounts } from '../../../Shared/src/pictures';
import importedStyles from './StoryEditor.module.scss';
const styles = importedStyles || ({} as Record<string, string>);

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Fit padding that keeps every node clear of the floating preview pane
 * (340 px wide, 12 px from the top-right corner) and of the status line.
 */
export const GRAPH_FIT_VIEW_OPTIONS: FitViewOptions = {
  padding: { top: '64px', right: '376px', bottom: '40px', left: '40px' },
  maxZoom: 1.25,
};

const AUTOSAVE_INTERVAL_MS = 30000;
const NOTICE_MS = 4000;

// =============================================================================
// CUSTOM NODE: SceneNode
// =============================================================================

interface SceneNodeData {
  nodeId: string;
  storySceneId: number;
  isStart: boolean;
  openSceneTab?: (sceneId: number, name: string) => void;
  [key: string]: unknown;
}

const NO_CHOICES: ChoiceOption[] = [];

// ── Duration formatter ──────────────────────────────────────────────────────
function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// ── Published-date formatter ─────────────────────────────────────────────────
function formatPublishedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ── SceneNodeComponent ──────────────────────────────────────────────────────
function SceneNodeComponent({ data, selected }: { data: SceneNodeData; selected?: boolean }) {
  const ss = useStoryStore((s) => s.story?.storyScenes.find((x) => x.id === data.storySceneId));
  // The badge, the connect picker and the edge panel share one source.
  const choices = useStoryStore((s) => s.sceneChoicesMap.get(data.storySceneId) ?? NO_CHOICES);
  const setStartNode = useStoryStore((s) => s.setStartNode);
  const runnerActive = useStoryStore((s) => s.previewRunnerActive);

  const duration = formatDuration(ss?.durationMs);
  const choiceCount = choices.length;

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (ss && data.openSceneTab) {
      data.openSceneTab(ss.sceneId, ss.sceneName || `Scene #${ss.sceneId}`);
    }
  };

  const handleSetStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setStartNode(data.nodeId);
  };

  return (
    <div
      className={`${styles.sceneNode} ${selected ? styles.selected : ''} ${data.isStart ? styles.startNode : ''}`}
      data-testid={`story-node-${data.storySceneId}`}
      data-start={data.isStart ? 'true' : 'false'}
    >
      <Handle type="target" position={Position.Top} />

      {/* Name row */}
      <div className={styles.nodeHeader}>
        <span className={styles.nodeName}>{ss?.sceneName || 'Unknown Scene'}</span>
        <button
          className={`${styles.nodeEditBtn} nodrag`}
          onClick={handleEditClick}
          title="Open in editor"
          aria-label="Open scene in a tab"
          data-testid={`node-edit-${data.storySceneId}`}
        >
          ✏
        </button>
      </div>

      {/* Meta row: duration */}
      <div className={styles.nodeMetaRow}>
        {duration && (
          <span className={styles.nodeDuration}>{duration}</span>
        )}
      </div>

      {/* Footer row: choices count + set-start button */}
      <div className={styles.nodeFooter}>
        {choiceCount > 0 && (
          <span className={styles.nodeChoicesBadge} data-testid={`node-choices-${data.storySceneId}`}>
            {choiceCount} {choiceCount === 1 ? 'choice' : 'choices'}
          </span>
        )}
        <button
          className={`${styles.nodeSetStartBtn} ${data.isStart ? styles.nodeSetStartActive : ''} nodrag`}
          onClick={handleSetStart}
          disabled={runnerActive}
          title={data.isStart ? 'Start scene' : 'Set as start'}
          aria-label={data.isStart ? 'Start scene' : 'Set as start'}
          aria-pressed={data.isStart}
          data-testid={`node-start-${data.storySceneId}`}
        >
          ⭐
        </button>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

const nodeTypes: NodeTypes = {
  sceneNode: SceneNodeComponent,
};

// =============================================================================
// AUTO-SAVE HOOK — every 30 s while dirty
// =============================================================================

function useAutoSave(intervalMs = AUTOSAVE_INTERVAL_MS) {
  const isDirty = useStoryStore((s) => s.isDirty);
  const saveStory = useStoryStore((s) => s.saveStory);

  useEffect(() => {
    if (!isDirty) return;
    const timer = setInterval(() => { void saveStory(); }, intervalMs);
    return () => clearInterval(timer);
  }, [isDirty, intervalMs, saveStory]);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface PublishStatus {
  version: number;
  publishedAt: string;
  token: string;
  /** From the Service (spec 011); never assembled here. */
  capabilityUrl: string;
  playerUrl: string;
  /** False when this server lacks the version's files (F-045). */
  filesPresent: boolean;
}

type PublishError = { kind: 'graph' | 'other'; message: string };

interface PendingConnection {
  source: string;
  target: string;
  choices: ChoiceOption[];
}

const StoryEditor: React.FC = () => {
  const { storyId } = useParams<{ storyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryView = new URLSearchParams(location.search).get('view');

  // The view (spec 025): a pending brief, then `?view=`, then the remembered
  // view, then the studio. Known on the first render, so no view flashes up.
  const [userId] = useState(sessionUserId);
  const [view, setView] = useState<StoryEditorView>(() => resolveStoryView({
    query: queryView,
    userId,
    pendingBrief: storyId !== undefined && useAssistantStore.getState().pendingBrief[storyId] !== undefined,
  }));
  const [viewSaveFailed, setViewSaveFailed] = useState(false);

  // Store
  const story = useStoryStore((s) => s.story);
  const isLoading = useStoryStore((s) => s.isLoading);
  const error = useStoryStore((s) => s.error);
  const isDirty = useStoryStore((s) => s.isDirty);
  const isSaving = useStoryStore((s) => s.isSaving);
  const saveFailed = useStoryStore((s) => s.saveFailed);
  const notice = useStoryStore((s) => s.notice);
  const validation = useStoryStore((s) => s.validation);
  const loadStory = useStoryStore((s) => s.loadStory);
  const saveStory = useStoryStore((s) => s.saveStory);
  const updateName = useStoryStore((s) => s.updateName);
  const commitName = useStoryStore((s) => s.commitName);
  const addSceneReference = useStoryStore((s) => s.addSceneReference);
  const copySceneToStory = useStoryStore((s) => s.copySceneToStory);
  const ownScene = useStoryStore((s) => s.ownScene);
  const createNewScene = useStoryStore((s) => s.createNewScene);
  const addSceneNode = useStoryStore((s) => s.addSceneNode);
  const removeStoryScene = useStoryStore((s) => s.removeStoryScene);
  const updateNodePosition = useStoryStore((s) => s.updateNodePosition);
  const removeNodes = useStoryStore((s) => s.removeNodes);
  const removeEdge = useStoryStore((s) => s.removeEdge);
  const removeElements = useStoryStore((s) => s.removeElements);
  const canUndo = useStoryStore((s) => s.canUndo);
  const canRedo = useStoryStore((s) => s.canRedo);
  const undo = useStoryStore((s) => s.undo);
  const redo = useStoryStore((s) => s.redo);
  const connect = useStoryStore((s) => s.connect);
  const setEdgeCondition = useStoryStore((s) => s.setEdgeCondition);
  const setNotice = useStoryStore((s) => s.setNotice);
  const reset = useStoryStore((s) => s.reset);
  const setDanglingEdges = useStoryStore((s) => s.setDanglingEdges);
  const danglingEdgeIds = useStoryStore((s) => s.danglingEdgeIds);
  const syncChoiceEdges = useStoryStore((s) => s.syncChoiceEdges);
  const sceneChoicesMap = useStoryStore((s) => s.sceneChoicesMap);
  const pictureMap = useStoryStore((s) => s.pictureMap);

  // Modal context
  const { addModal, removeModal } = useModalContext();

  // Add scene dropdown
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const addDropdownRef = useRef<HTMLDivElement>(null);

  // Tab state: 'graph' or a sceneId number
  const [activeTab, setActiveTab] = useState<'graph' | number>('graph');
  const [previewOrientation, setPreviewOrientation] = useState<OrientationMode>('portrait');
  const [openTabs, setOpenTabs] = useState<Array<{ sceneId: number; name: string }>>([]);
  // The story runner preview (spec 009): while it plays, edge editing is locked
  const runnerActive = useStoryStore((s) => s.previewRunnerActive);
  const setRunnerActive = useStoryStore((s) => s.setPreviewRunnerActive);
  // A batch of story operations is in flight (spec 023): the map is read-only
  const batchPending = useStoryStore((s) => s.batchPending);

  // React Flow state: derived from the store, plus measurements and selection
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  // While the runner plays an edge cannot be selected (F-038)
  const flowEdges = useMemo(
    () => (runnerActive ? edges.map((e) => ({ ...e, selectable: false, selected: false, focusable: false })) : edges),
    [edges, runnerActive],
  );

  useAutoSave();

  // The story on a phone (spec 013 v7): sends story-data while the device plays a story run.
  useDeviceStorySync(story);

  // Track selected node for preview pane; in the studio it is the selected scene (spec 025)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNodeIdRef = useRef(selectedNodeId);
  selectedNodeIdRef.current = selectedNodeId;
  // Spec 031 v3 D-18: on the map, the selected scene node is the scene the author looks at.
  const hoodSelectedStorySceneId = useMemo(() => {
    const node = selectedNodeId ? story?.graph.nodes.find((n) => n.id === selectedNodeId) : undefined;
    if (!node || node.type === 'end' || node.storySceneId === undefined) return undefined;
    return story?.storyScenes.some((ss) => ss.id === node.storySceneId) ? node.storySceneId : undefined;
  }, [selectedNodeId, story]);
  // The story hood's view: the map with the preview pane's orientation while a scene is selected; no moment.
  const hoodViewRef = useRef({ sceneShown: false, orientation: previewOrientation });
  hoodViewRef.current = { sceneShown: hoodSelectedStorySceneId !== undefined, orientation: previewOrientation };
  useAssistantView(view === 'hood' && activeTab === 'graph', () => (hoodViewRef.current.sceneShown
    ? { surface: 'hood', orientation: hoodViewRef.current.orientation }
    : { surface: 'hood' }));

  // Pending connection awaiting a choice
  const [pendingConnection, setPendingConnection] = useState<PendingConnection | null>(null);

  // Selected edge for the properties panel
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);

  // Scene removal awaiting confirmation
  const [pendingRemoval, setPendingRemoval] = useState<StorySceneWithScene | null>(null);

  // Validation problem list
  const [showProblems, setShowProblems] = useState(false);

  // Publish state
  const [publishStatus, setPublishStatus] = useState<PublishStatus | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<PublishError | null>(null);
  /** Set only by a publish that succeeded in this session (F-032). */
  const [justPublishedUrl, setJustPublishedUrl] = useState<string | null>(null);
  const [copyDone, setCopyDone] = useState(false);
  /** The Share dialog (spec 015): opened from the banner or the badge, never on its own. */
  const [shareOpen, setShareOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The graph a "fix the graph" publish error was raised against. */
  const failedGraphRef = useRef<unknown>(null);

  const onSelectionChange = useCallback(({ nodes: selNodes }: OnSelectionChangeParams) => {
    setSelectedNodeId(selNodes.length === 1 ? selNodes[0].id : null);
  }, []);

  // ── The view switch (spec 025) ───────────────────────────────────────────
  const viewRef = useRef(view);
  viewRef.current = view;
  const switching = useRef(false);
  /** The `view` query this editor last saw or wrote; only a change from outside switches. */
  const lastQuery = useRef(queryView);
  const replaceViewQuery = useCallback((next: StoryEditorView) => {
    const params = new URLSearchParams(location.search);
    if (params.get('view') === next) return;
    params.set('view', next);
    // The Pictures view's own keys (026) leave with it.
    if (next !== 'pictures') for (const k of ['by', 'filter', 'slot']) params.delete(k);
    lastQuery.current = next;
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true, state: location.state });
  }, [location.pathname, location.search, location.state, navigate]);

  /** Lossless: a dirty scene is saved first (a failed save keeps the view), the selection carries over. */
  const changeView = useCallback(async (next: StoryEditorView, opts: { remember: boolean }) => {
    if (next === viewRef.current || switching.current) return;
    switching.current = true;
    try {
      const sc = useSceneStore.getState();
      if (sc.scene && sc.isDirty) {
        cacheDraft();
        await sc.saveScene();
        const after = useSceneStore.getState();
        if (after.isDirty && after.saveStatus === 'error') {
          setViewSaveFailed(true);
          replaceViewQuery(viewRef.current);
          return;
        }
      }
      setViewSaveFailed(false);
      if (next === 'hood') {
        // The studio's scene is the selected node on the map.
        setNodes((prev) => prev.map((n) => (n.selected === (n.id === selectedNodeIdRef.current) ? n : { ...n, selected: n.id === selectedNodeIdRef.current })));
      }
      useStoryStore.getState().setPreviewRunnerActive(false);
      setView(next);
      // The Pictures view is never remembered (026 D-6).
      if (opts.remember && isStoryView(next)) writeStoredView(userId, next);
      replaceViewQuery(next);
    } finally {
      switching.current = false;
    }
  }, [replaceViewQuery, userId, setNodes]);
  const chooseView = useCallback((next: StoryEditorView) => { void changeView(next, { remember: true }); }, [changeView]);
  const openHood = useCallback(() => chooseView('hood'), [chooseView]);

  // The URL's `view` query, changed from outside (back/forward, a link), switches the view.
  useEffect(() => {
    if (queryView === lastQuery.current) return;
    lastQuery.current = queryView;
    if (isStoryEditorView(queryView) && queryView !== viewRef.current) void changeView(queryView, { remember: false });
    // Only a change of the query itself, never a change of view, drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryView]);
  // A pending brief opened the studio over `?view=hood`: the URL says so too.
  useEffect(() => {
    if (isStoryEditorView(queryView) && queryView !== viewRef.current) replaceViewQuery(viewRef.current);
    // On mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pop-out preview window and its channel (spec 009)
  const { poppedOut, popOut, popBack } = useStoryPreviewPopout(
    storyId ? parseInt(storyId, 10) : null,
    previewOrientation,
    setPreviewOrientation,
  );

  // Starting the runner closes the edge panel (F-038); selecting a node, or
  // leaving the graph tab, stops it
  useEffect(() => {
    if (runnerActive) setSelectedEdgeId(null);
  }, [runnerActive]);
  useEffect(() => {
    if (selectedNodeId && useStoryStore.getState().previewRunnerActive) setRunnerActive(false);
  }, [selectedNodeId, setRunnerActive]);
  useEffect(() => {
    if (activeTab !== 'graph') setRunnerActive(false);
  }, [activeTab, setRunnerActive]);

  // Load story on mount
  useEffect(() => {
    if (storyId) loadStory(parseInt(storyId, 10));
    return () => { reset(); };
  }, [storyId, loadStory, reset]);

  // The graph tab's left panel (spec 024 D-5)
  const storyPanelTab = useAssistantStore((s) => s.storyPanelTab);
  const setStoryPanelTab = useAssistantStore((s) => s.setStoryPanelTab);

  // A brief from the start sheet (spec 024): its story's first turn, sent once.
  const loadedStoryId = story?.id;
  useEffect(() => {
    if (loadedStoryId === undefined || String(loadedStoryId) !== storyId) return;
    const assistant = useAssistantStore.getState();
    const brief = assistant.takePendingBrief(loadedStoryId);
    if (brief === undefined) return;
    // A brief lands in the studio (spec 025), whatever the URL says.
    if (viewRef.current !== 'studio') {
      setView('studio');
      viewRef.current = 'studio';
    }
    if (queryView && queryView !== 'studio') replaceViewQuery('studio');
    setActiveTab('graph');
    assistant.setStoryPanelTab('assistant');
    void assistant.sendStory(loadedStoryId, brief, { fromBrief: true });
    // Runs once per loaded story; the query and its writer are read as they are then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedStoryId, storyId]);

  // Load publish status on mount: the badge only, never the "Published!" banner
  useEffect(() => {
    if (!storyId) return;
    const id = parseInt(storyId, 10);
    apiClient.getPublishedStory(id).then((res) => {
      if (res.success && res.data) {
        setPublishStatus({
          version: res.data.version,
          publishedAt: res.data.publishedAt,
          token: res.data.token,
          capabilityUrl: res.data.capabilityUrl,
          playerUrl: res.data.playerUrl,
          filesPresent: res.data.filesPresent !== false,
        });
      }
    }).catch(() => { /* silent — status badge just stays "Not published" */ });
  }, [storyId]);

  // Set of storySceneIds currently on the graph
  const graphStorySceneIds = useMemo(
    () => new Set(story?.graph.nodes.map((n) => n.storySceneId) ?? []),
    [story?.graph.nodes]
  );

  // Tab helper
  const openSceneTab = useCallback((sceneId: number, name: string) => {
    setOpenTabs((prev) => {
      if (prev.some((t) => t.sceneId === sceneId)) return prev;
      return [...prev, { sceneId, name }];
    });
    setActiveTab(sceneId);
  }, []);

  // Store → React Flow. Positions, labels, start marker and dangling state all
  // come from the store; React Flow keeps only its measurements and selection.
  const graph = story?.graph;
  useEffect(() => {
    if (!graph) {
      setNodes([]);
      setEdges([]);
      return;
    }
    const nextNodes = toReactFlowNodes(graph, openSceneTab);
    setNodes((prev) => {
      const byId = new Map(prev.map((n) => [n.id, n]));
      return nextNodes.map((n) => {
        const p = byId.get(n.id);
        return p ? { ...n, measured: p.measured, selected: p.selected } : n;
      });
    });
    const nextEdges = toReactFlowEdges(graph, danglingEdgeIds);
    setEdges((prev) => {
      const byId = new Map(prev.map((e) => [e.id, e]));
      return nextEdges.map((e) => ({ ...e, selected: byId.get(e.id)?.selected ?? false }));
    });
  }, [graph, danglingEdgeIds, openSceneTab, setNodes, setEdges]);

  // A scene tab closes when its scene left the story (a batch undone, spec 023)
  const storyScenes = story?.storyScenes;
  useEffect(() => {
    if (!storyScenes) return;
    const present = new Set(storyScenes.map((ss) => ss.sceneId));
    setOpenTabs((prev) => {
      const kept = prev.filter((t) => present.has(t.sceneId));
      return kept.length === prev.length ? prev : kept;
    });
    setActiveTab((cur) => (typeof cur === 'number' && !present.has(cur) ? 'graph' : cur));
  }, [storyScenes]);

  // The edge panel closes when its edge is gone
  useEffect(() => {
    if (selectedEdgeId && !graph?.edges.some((e) => e.id === selectedEdgeId)) {
      setSelectedEdgeId(null);
    }
  }, [graph, selectedEdgeId]);

  // Notices clear themselves
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(t);
  }, [notice, setNotice]);

  // A "fix the graph" publish error clears as soon as the graph is fixed (F-036)
  useEffect(() => {
    if (publishError?.kind !== 'graph' || !validation?.valid) return;
    if (graph !== failedGraphRef.current) {
      setPublishError(null);
      failedGraphRef.current = null;
    }
  }, [graph, validation?.valid, publishError]);

  // Back navigation — always go to the story's folder in the organiser
  const handleBackNavigation = useCallback(() => {
    if (story?.folderId) {
      navigate(`/folder/${story.folderId}`);
    } else {
      navigate('/');
    }
  }, [navigate, story?.folderId]);

  const problemCount = validation?.errors.length ?? 0;

  // Publishing with placeholders (spec 026 D-4): a confirm first when pictures are still needed.
  const [placeholderConfirm, setPlaceholderConfirm] = useState<number | null>(null);

  // Publish: save pending changes first, then publish
  const publishNow = useCallback(async () => {
    if (!story || isPublishing || problemCount > 0) return;
    setIsPublishing(true);
    setPublishError(null);
    setJustPublishedUrl(null);
    try {
      if (isDirty) {
        const saved = await saveStory();
        if (!saved) {
          setPublishError({ kind: 'other', message: 'Your changes could not be saved, so nothing was published.' });
          return;
        }
      }
      const res = await apiClient.publishStory(story.id);
      if (res.success) {
        setJustPublishedUrl(res.data.capabilityUrl);
        setPublishStatus({
          version: res.data.version,
          publishedAt: res.data.publishedAt,
          token: res.data.token,
          capabilityUrl: res.data.capabilityUrl,
          playerUrl: res.data.playerUrl,
          filesPresent: true,
        });
        setDanglingEdges([]);
      } else {
        // A graph rejection carries danglingEdgeIds
        const failure = res as { success: false; error: string; danglingEdgeIds?: string[] };
        if (Array.isArray(failure.danglingEdgeIds)) {
          setDanglingEdges(failure.danglingEdgeIds);
          failedGraphRef.current = story.graph;
          setPublishError({ kind: 'graph', message: 'Fix highlighted edges before publishing' });
        } else {
          setPublishError({ kind: 'other', message: failure.error ?? 'Publish failed' });
        }
      }
    } catch (err) {
      setPublishError({ kind: 'other', message: err instanceof Error ? err.message : 'Publish failed' });
    } finally {
      setIsPublishing(false);
    }
  }, [story, isPublishing, problemCount, isDirty, saveStory, setDanglingEdges]);

  const handlePublish = useCallback(() => {
    if (!story || isPublishing || problemCount > 0) return;
    const { needed } = pictureMapCounts(pictureMap);
    if (needed > 0) {
      setPlaceholderConfirm(needed);
      return;
    }
    void publishNow();
  }, [story, isPublishing, problemCount, pictureMap, publishNow]);

  const publishedUrl = publishStatus?.capabilityUrl ?? null;

  // Badge click — copy the capability URL with inline feedback (F-033)
  const handleBadgeClick = useCallback(async () => {
    if (!publishedUrl) return;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(publishedUrl);
      setCopyFailed(false);
      setIsCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 1500);
    } catch {
      setIsCopied(false);
      setCopyFailed(true);
    }
  }, [publishedUrl]);

  useEffect(() => () => {
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
  }, []);

  // Copy the just-published URL from the banner
  const handleCopyUrl = useCallback(() => {
    if (!justPublishedUrl) return;
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(justPublishedUrl))
      .then(() => {
        setCopyDone(true);
        setTimeout(() => setCopyDone(false), 2000);
      })
      .catch(() => { /* the URL stays selectable in the banner */ });
  }, [justPublishedUrl]);

  // ── React Flow → store ──────────────────────────────────────────────────
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (useStoryStore.getState().previewRunnerActive) {
        changes = changes.filter((c) => c.type !== 'remove');
      }
      onNodesChange(changes);
      const removed = changes.filter((c) => c.type === 'remove').map((c) => (c as { id: string }).id);
      if (removed.length > 0) removeNodes(removed);
    },
    [onNodesChange, removeNodes]
  );

  // One drag is one history entry, however many nodes it moves (spec 010)
  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, _node: Node, dragged: Node[]) => {
      const { beginHistoryGroup, endHistoryGroup } = useStoryStore.getState();
      beginHistoryGroup();
      try {
        for (const n of dragged) updateNodePosition(n.id, { x: n.position.x, y: n.position.y });
      } finally {
        endHistoryGroup();
      }
    },
    [updateNodePosition]
  );

  // Delete/Backspace: React Flow reports node and edge removals separately;
  // the store removes everything one keypress takes as one history entry and
  // React Flow re-renders from it (spec 010).
  const handleBeforeDelete: OnBeforeDelete = useCallback(
    async ({ nodes: delNodes, edges: delEdges }) => {
      if (!useStoryStore.getState().previewRunnerActive) {
        removeElements(delNodes.map((n) => n.id), delEdges.map((e) => e.id));
      }
      return false;
    },
    [removeElements]
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // While the runner plays edges are neither selected nor deleted
      if (useStoryStore.getState().previewRunnerActive) {
        changes = changes.filter((c) => c.type !== 'remove' && c.type !== 'select');
      }
      onEdgesChange(changes);
      for (const c of changes) {
        if (c.type === 'remove') removeEdge(c.id);
      }
    },
    [onEdgesChange, removeEdge]
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      if (useStoryStore.getState().previewRunnerActive) return;
      const sourceNode = story?.graph.nodes.find((n) => n.id === connection.source);
      const choices = sourceNode ? sceneChoicesMap.get(sourceNode.storySceneId) ?? [] : [];
      if (choices.length > 0) {
        setPendingConnection({
          source: connection.source,
          target: connection.target,
          choices: availableChoices({ story, sceneChoicesMap }, connection.source),
        });
        return;
      }
      const result = connect(connection.source, connection.target);
      if (!result.ok) setNotice(result.message);
    },
    [story, sceneChoicesMap, connect, setNotice]
  );

  const handleChoicePick = useCallback(
    (choiceId: string) => {
      if (!pendingConnection) return;
      const result = connect(pendingConnection.source, pendingConnection.target, choiceId);
      if (!result.ok) setNotice(result.message);
      setPendingConnection(null);
    },
    [pendingConnection, connect, setNotice]
  );

  const cancelChoicePick = useCallback(() => {
    setPendingConnection(null);
  }, []);

  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: Edge) => {
    if (useStoryStore.getState().previewRunnerActive) return;
    setSelectedEdgeId(edge.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedEdgeId(null);
  }, []);

  const handleEdgeConditionChange = useCallback(
    (value: string) => {
      if (!selectedEdgeId) return;
      if (!setEdgeCondition(selectedEdgeId, value || null)) {
        setNotice(value ? 'That choice already has an arrow' : 'This scene cannot have an unconditional arrow');
      }
    },
    [selectedEdgeId, setEdgeCondition, setNotice]
  );

  // ── Scenes ──────────────────────────────────────────────────────────────
  const closeSceneTab = useCallback((sceneId: number) => {
    // Closing a tab never discards: the unmounting SceneEditor caches a draft
    // and saves it (spec 004).
    setOpenTabs((prev) => prev.filter((t) => t.sceneId !== sceneId));
    setActiveTab((cur) => (cur === sceneId ? 'graph' : cur));
  }, []);

  const handleOpenScene = useCallback(
    (ss: StorySceneWithScene) => {
      openSceneTab(ss.sceneId, ss.sceneName || `Scene #${ss.sceneId}`);
    },
    [openSceneTab]
  );

  // Spec 028: a Changes card's Open opens that scene's tab.
  const openChangedScene = useCallback((storySceneId: number) => {
    const ss = useStoryStore.getState().story?.storyScenes.find((x) => x.id === storySceneId);
    if (ss) openSceneTab(ss.sceneId, ss.sceneName || `Scene #${ss.sceneId}`);
  }, [openSceneTab]);

  // "+ New scene" — create `Scene N`, own it, place its node and open it
  const handleNewScene = useCallback(async () => {
    setShowAddDropdown(false);
    const ss = await createNewScene();
    if (ss) openSceneTab(ss.sceneId, ss.sceneName || `Scene #${ss.sceneId}`);
  }, [createNewScene, openSceneTab]);

  // "Existing scene" — open the scene picker modal
  const handleExistingScene = useCallback(() => {
    if (!story) return;
    setShowAddDropdown(false);
    const existingSceneIds = story.storyScenes.map((ss) => ss.sceneId);

    addModal({
      title: 'Add Existing Scene',
      size: 'lg',
      body: (
        <ScenePicker
          existingSceneIds={existingSceneIds}
          onSelect={async (sceneId, mode) => {
            removeModal();
            let ss: StorySceneWithScene | null = null;
            if (mode === 'reference') {
              ss = await addSceneReference(sceneId);
            } else if (mode === 'copy-own') {
              ss = await copySceneToStory(sceneId);
            } else if (mode === 'move-own') {
              ss = await ownScene(sceneId);
            }
            if (ss) addSceneNode(ss.id);
          }}
          onCancel={() => removeModal()}
        />
      ),
    });
  }, [story, addModal, removeModal, addSceneReference, copySceneToStory, ownScene, addSceneNode]);

  const confirmRemoveStoryScene = useCallback(async () => {
    if (!pendingRemoval) return;
    const ok = await removeStoryScene(pendingRemoval.id);
    if (!ok) throw new Error('The scene could not be removed from the story. Try again.');
    closeSceneTab(pendingRemoval.sceneId);
  }, [pendingRemoval, removeStoryScene, closeSceneTab]);

  const handleNodeDoubleClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const ss = story?.storyScenes.find((s) => s.id === (node.data as SceneNodeData).storySceneId);
      if (ss) openSceneTab(ss.sceneId, ss.sceneName || `Scene #${ss.sceneId}`);
    },
    [story?.storyScenes, openSceneTab]
  );

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        addDropdownRef.current &&
        !addDropdownRef.current.contains(e.target as globalThis.Node)
      ) {
        setShowAddDropdown(false);
      }
    };
    if (showAddDropdown) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showAddDropdown]);

  // Undo / redo on the graph tab; a scene tab keeps its own scene undo (spec 010)
  // (The studio has its own: Cmd/Ctrl+Z acts on the scene or the story by focus.)
  useStoryShortcuts(view === 'hood' && activeTab === 'graph');

  // Keyboard save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const cmd = navigator.platform.toUpperCase().includes('MAC') ? e.metaKey : e.ctrlKey;
      if (cmd && e.key === 's' && (activeTab === 'graph' || view === 'studio')) {
        e.preventDefault();
        void saveStory();
        if (view === 'studio') {
          const sc = useSceneStore.getState();
          if (sc.scene && sc.isDirty) void sc.saveScene();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [saveStory, activeTab, view]);

  // Warn on unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Bridge: when the embedded SceneEditor completes a save (isDirty: true → false),
  // sync the scene's choices into the story so badges, picker and labels update.
  useEffect(() => {
    const unsubscribe = useSceneStore.subscribe(
      (state) => state.isDirty,
      (sceneDirty, prevSceneDirty) => {
        // Only the hood's active scene tab (the studio syncs its own scene).
        if (prevSceneDirty && !sceneDirty && typeof activeTab === 'number' && Number(useSceneStore.getState().scene?.id) === activeTab) {
          const storyScene = story?.storyScenes.find((ss) => ss.sceneId === activeTab);
          if (storyScene) {
            const choices = useSceneStore.getState().scene?.choices ?? [];
            syncChoiceEdges(storyScene.id, choices);
          }
        }
      }
    );
    return unsubscribe;
  }, [activeTab, story?.storyScenes, syncChoiceEdges]);

  // Resolves activeTab → storySceneId, then calls syncChoiceEdges.
  const syncFromStore = useCallback(() => {
    if (typeof activeTab !== 'number') return;
    const state = useSceneStore.getState();
    if (state.scene?.id !== activeTab || state.isLoading) return;
    const storyScene = story?.storyScenes.find((ss) => ss.sceneId === activeTab);
    if (!storyScene) return;
    syncChoiceEdges(storyScene.id, state.scene?.choices ?? []);
  }, [activeTab, story?.storyScenes, syncChoiceEdges]);

  // Sync choices on scene load, not just on save.
  useEffect(() => {
    syncFromStore();
    const unsubscribe = useSceneStore.subscribe(
      (state) => state.isLoading,
      (loading, prevLoading) => {
        if (prevLoading && !loading) syncFromStore();
      }
    );
    return unsubscribe;
  }, [syncFromStore]);

  // =========================================================================
  // RENDER
  // =========================================================================

  if (isLoading && !story) {
    return (
      <div className={styles.loadingState}>
        <div className={styles.spinner} />
        <p>Loading story...</p>
      </div>
    );
  }

  if (error && !story) {
    return (
      <div className={styles.errorState}>
        <p className={styles.errorMessage}>Error: {error}</p>
        <button className={styles.goBackButton} onClick={handleBackNavigation}>Go Back</button>
      </div>
    );
  }

  if (!story) {
    return (
      <div className={styles.emptyState}>
        <p>Story not found</p>
        <button className={styles.goBackButton} onClick={handleBackNavigation}>Go Back</button>
      </div>
    );
  }

  const selectedEdge = selectedEdgeId ? story.graph.edges.find((e) => e.id === selectedEdgeId) : undefined;
  const warnings = validation?.warnings ?? [];
  const errors = validation?.errors ?? [];

  return (
    <div className={styles.editor}>
      {/* Toolbar */}
      <header className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <button className={styles.backButton} onClick={handleBackNavigation} title="Back">
            ←
          </button>
          <div className={styles.storyName}>
            <input
              type="text"
              value={story.name}
              onChange={(e) => updateName(e.target.value)}
              onBlur={() => commitName()}
              className={styles.nameInput}
              aria-label="Story name"
            />
            {saveFailed ? (
              <span className={styles.saveFailed} role="status" data-testid="story-save-status">
                Not saved — retrying
              </span>
            ) : isSaving ? (
              <span className={styles.savingIndicator} role="status" data-testid="story-save-status">Saving…</span>
            ) : (
              isDirty && <span className={styles.unsavedIndicator} title="Unsaved changes">●</span>
            )}
          </div>
          {/* The view switch (spec 025) */}
          <div className={styles.viewSwitch} role="group" aria-label="View" data-testid="view-switch">
            <button
              type="button"
              className={`${styles.viewSwitchButton} ${view === 'studio' ? styles.viewSwitchOn : ''}`}
              aria-pressed={view === 'studio'}
              onClick={() => chooseView('studio')}
              data-testid="view-studio"
            >
              Story studio
            </button>
            <button
              type="button"
              className={`${styles.viewSwitchButton} ${view === 'pictures' ? styles.viewSwitchOn : ''}`}
              aria-pressed={view === 'pictures'}
              onClick={() => chooseView('pictures')}
              data-testid="view-pictures"
            >
              Pictures
            </button>
            <button
              type="button"
              className={`${styles.viewSwitchButton} ${view === 'hood' ? styles.viewSwitchOn : ''}`}
              aria-pressed={view === 'hood'}
              onClick={() => chooseView('hood')}
              data-testid="view-hood"
            >
              Under the hood
            </button>
          </div>
          {viewSaveFailed && (
            <span className={styles.saveFailed} role="alert" data-testid="view-save-failed" title={useSceneStore.getState().saveError ?? undefined}>
              Save failed — the view stays until it saves
            </span>
          )}
        </div>

        <div className={styles.toolbarRight}>
          {/* Publish status badge — click to copy the capability URL when published */}
          {publishStatus && !publishStatus.filesPresent ? (
            // The row is shared by every stack on this DB, the files are not (spec 011, F-045).
            <div className={styles.publishBadge} data-testid="publish-badge" role="status">
              Files missing on this server — publish again
            </div>
          ) : publishStatus ? (
            <button
              type="button"
              className={`${styles.publishBadge} ${styles.publishBadgeClickable}`}
              onClick={handleBadgeClick}
              title="Click to copy capability URL"
              data-testid="publish-badge"
            >
              {isCopied
                ? 'Copied!'
                : copyFailed
                  ? 'Copy failed — select the URL'
                  : `v${publishStatus.version} · ${formatPublishedAt(publishStatus.publishedAt)}`}
            </button>
          ) : (
            <div className={styles.publishBadge} data-testid="publish-badge">Not published</div>
          )}
          {publishStatus?.filesPresent && publishStatus.playerUrl && (
            <a
              className={styles.publishBadge}
              href={publishStatus.playerUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="publish-player-link"
              title="Open the published story in a new tab"
            >
              Open ↗
            </a>
          )}
          {publishStatus?.filesPresent && (
            <button
              type="button"
              className={`${styles.publishBadge} ${styles.publishBadgeClickable}`}
              onClick={() => setShareOpen(true)}
              data-testid="publish-share"
              title="Player link, embed code and integration URL"
            >
              Share…
            </button>
          )}
          {copyFailed && publishedUrl && (
            <input
              type="text"
              readOnly
              value={publishedUrl}
              className={styles.capabilityUrlInput}
              aria-label="Published URL"
              autoFocus
              onFocus={(e) => e.target.select()}
              onBlur={() => setCopyFailed(false)}
            />
          )}

          {/* The story on a phone (spec 013): one button for both views (D-3) */}
          <StoryDeviceButton
            story={story}
            view={view === 'hood' ? 'hood' : 'studio'}
            selectedNodeId={selectedNodeId}
            activeSceneId={view === 'hood' && typeof activeTab === 'number' ? activeTab : null}
            className={styles.toolbarButton}
          />

          {/* Story history (spec 010) */}
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={() => { void undo(); }}
            disabled={!canUndo || runnerActive || batchPending || (view === 'hood' && activeTab !== 'graph')}
            aria-label="Undo"
            title="Undo (Cmd/Ctrl+Z)"
            data-testid="story-undo"
          >
            ↶
          </button>
          <button
            type="button"
            className={styles.toolbarButton}
            onClick={() => { void redo(); }}
            disabled={!canRedo || runnerActive || batchPending || (view === 'hood' && activeTab !== 'graph')}
            aria-label="Redo"
            title="Redo (Cmd/Ctrl+Shift+Z)"
            data-testid="story-redo"
          >
            ↷
          </button>

          {/* Publish button */}
          <button
            className={`${styles.toolbarButton} ${styles.publishButton}`}
            onClick={handlePublish}
            disabled={isPublishing || problemCount > 0}
            aria-label="Publish story"
            title={problemCount > 0 ? `Fix ${problemCount} ${problemCount === 1 ? 'problem' : 'problems'} first` : undefined}
          >
            {isPublishing ? (
              <><span className={styles.spinnerInline} aria-hidden="true" /> Publishing…</>
            ) : (
              'Publish'
            )}
          </button>

          <button
            className={`${styles.toolbarButton} ${styles.saveButton}`}
            onClick={() => { void saveStory(); }}
            disabled={!isDirty || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </header>

      {/* Publish error */}
      {publishError && (
        <div className={styles.publishErrorBanner} role="alert">
          <span>{publishError.message}</span>
          <button
            className={styles.capabilityBannerDismiss}
            onClick={() => setPublishError(null)}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      {/* Published banner — only after a publish in this session */}
      {justPublishedUrl && (
        <div className={styles.capabilityBanner}>
          <span className={styles.capabilityBannerLabel}>Published! Capability URL:</span>
          <label htmlFor="capability-url-input" className={styles.srOnly}>Capability URL</label>
          <input
            id="capability-url-input"
            type="text"
            readOnly
            value={justPublishedUrl}
            className={styles.capabilityUrlInput}
            aria-label="Capability URL"
            onFocus={(e) => e.target.select()}
          />
          <button
            className={styles.toolbarButton}
            onClick={handleCopyUrl}
            aria-label="Copy capability URL"
          >
            {copyDone ? 'Copied!' : 'Copy'}
          </button>
          {publishStatus && (
            <button
              className={styles.toolbarButton}
              onClick={() => setShareOpen(true)}
              data-testid="banner-share"
            >
              Share…
            </button>
          )}
          <button
            className={styles.capabilityBannerDismiss}
            onClick={() => setJustPublishedUrl(null)}
            aria-label="Dismiss banner"
          >
            ✕
          </button>
        </div>
      )}

      {placeholderConfirm !== null && (
        <div className={styles.publishErrorBanner} role="alertdialog" aria-label="Publish with placeholders" data-testid="publish-placeholders-confirm">
          <span>
            <strong>{placeholderConfirm === 1 ? '1 picture is still a placeholder' : `${placeholderConfirm} pictures are still placeholders`}</strong>
            {' '}Learners will see the character's default picture or a labelled placeholder in those places. You can publish now and fill them later.
          </span>
          <button
            className={styles.toolbarButton}
            onClick={() => { setPlaceholderConfirm(null); void publishNow(); }}
            data-testid="publish-anyway"
          >
            Publish anyway
          </button>
          <button
            className={styles.toolbarButton}
            onClick={() => {
              setPlaceholderConfirm(null);
              navigate({ pathname: location.pathname, search: picturesSearch({ filter: 'needed' }) }, { state: location.state });
            }}
            data-testid="publish-fill-pictures"
          >
            Fill pictures
          </button>
          <button className={styles.toolbarButton} onClick={() => setPlaceholderConfirm(null)} data-testid="publish-cancel">
            Cancel
          </button>
        </div>
      )}

      {view === 'pictures' ? (
        <PicturesView story={story} />
      ) : view === 'studio' ? (
        <StoryStudio
          story={story}
          selectedNodeId={selectedNodeId}
          onSelectNode={setSelectedNodeId}
          onOpenHood={openHood}
          userId={userId}
        />
      ) : (
      <>
      {/* Tab bar */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'graph' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('graph')}
        >
          <span className={styles.tabIcon}>🗺️</span>
          Graph
        </button>
        {openTabs.map((tab) => (
          <button
            key={tab.sceneId}
            className={`${styles.tab} ${activeTab === tab.sceneId ? styles.activeTab : ''}`}
            onClick={() => setActiveTab(tab.sceneId)}
            data-testid={`scene-tab-${tab.sceneId}`}
          >
            <span className={styles.tabIcon}>🎬</span>
            <span className={styles.tabName}>{tab.name}</span>
            <span
              className={styles.tabClose}
              onClick={(e) => { e.stopPropagation(); closeSceneTab(tab.sceneId); }}
            >
              ✕
            </span>
          </button>
        ))}
      </div>

      {/* Main content — Graph view or embedded SceneEditor */}
      {activeTab === 'graph' ? (
        <div className={styles.mainContent}>
          {/* Left panel — Scene list */}
          <aside className={`${styles.leftPanel} ${storyPanelTab === 'assistant' ? styles.leftPanelWide : ''}`}>
            <div className={styles.panelTabs} role="tablist" aria-label="Left panel">
              <button
                type="button"
                role="tab"
                aria-selected={storyPanelTab === 'scenes'}
                className={`${styles.panelTab} ${storyPanelTab === 'scenes' ? styles.panelTabActive : ''}`}
                onClick={() => setStoryPanelTab('scenes')}
                data-testid="left-tab-scenes"
              >
                Scenes
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={storyPanelTab === 'assistant'}
                className={`${styles.panelTab} ${storyPanelTab === 'assistant' ? styles.panelTabActive : ''}`}
                onClick={() => setStoryPanelTab('assistant')}
                data-testid="left-tab-assistant"
              >
                Assistant
              </button>
            </div>
            {storyPanelTab === 'assistant' ? (
              <StoryAssistantPanel storyId={story.id} selectedSceneId={hoodSelectedStorySceneId} onOpenScene={openChangedScene} />
            ) : (
            <>
            <div className={styles.panelHeader}>
              <h3>Scenes</h3>
              <div style={{ position: 'relative' }} ref={addDropdownRef}>
                <button
                  className={styles.addSceneButton}
                  onClick={() => setShowAddDropdown(!showAddDropdown)}
                  title="Add scene to story"
                >
                  +
                </button>
                {showAddDropdown && (
                  <div className={styles.addSceneDropdown}>
                    <button
                      className={styles.dropdownItem}
                      onClick={handleNewScene}
                    >
                      New scene
                      <span className={styles.dropdownHint}>Creates a new scene owned by this story</span>
                    </button>
                    <button
                      className={styles.dropdownItem}
                      onClick={handleExistingScene}
                    >
                      Existing scene
                      <span className={styles.dropdownHint}>Search &amp; link an existing scene</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.sceneList}>
              {story.storyScenes.length === 0 ? (
                <div className={styles.emptySceneList}>
                  <p>No scenes yet</p>
                  <p>Click + to add scenes to this story</p>
                </div>
              ) : (
                story.storyScenes.map((ss) => {
                  const isOnGraph = graphStorySceneIds.has(ss.id);
                  return (
                    <div
                      key={ss.id}
                      className={`${styles.sceneItem} ${styles[ss.mode]}`}
                      onClick={() => handleOpenScene(ss)}
                      data-testid={`story-scene-${ss.id}`}
                    >
                      <div className={styles.sceneItemInfo}>
                        <span className={styles.sceneName}>{ss.sceneName || `Scene #${ss.sceneId}`}</span>
                        <span className={styles.sceneMode}>{ss.mode}</span>
                      </div>
                      <div className={styles.sceneActions}>
                        {!isOnGraph && (
                          <button
                            className={styles.sceneActionButton}
                            onClick={(e) => { e.stopPropagation(); addSceneNode(ss.id); }}
                            title="Add to graph"
                            aria-label="Add to graph"
                          >
                            ＋
                          </button>
                        )}
                        <button
                          className={`${styles.sceneActionButton} ${styles.danger}`}
                          onClick={(e) => { e.stopPropagation(); setPendingRemoval(ss); }}
                          title="Remove from story"
                          aria-label="Remove from story"
                          data-testid={`remove-story-scene-${ss.id}`}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            </>
            )}
          </aside>

          {/* Graph canvas */}
          <div className={styles.graphArea}>
            {/* Live validation status line */}
            <div className={styles.graphStatus} data-testid="graph-status">
              {errors.length === 0 ? (
                <span className={styles.graphStatusReady}>Ready to publish</span>
              ) : (
                <button
                  type="button"
                  className={styles.graphStatusErrors}
                  onClick={() => setShowProblems((v) => !v)}
                  aria-expanded={showProblems}
                >
                  {errors.length} {errors.length === 1 ? 'problem' : 'problems'}
                </button>
              )}
              {warnings.length > 0 && (
                <span className={styles.graphStatusWarnings} title={warnings.join('\n')}>
                  {warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'}
                </span>
              )}
              {showProblems && errors.length > 0 && (
                <ul className={styles.graphProblems} data-testid="graph-problems">
                  {errors.map((msg, i) => <li key={`e${i}`}>{msg}</li>)}
                  {warnings.map((msg, i) => <li key={`w${i}`} className={styles.graphProblemWarning}>{msg}</li>)}
                </ul>
              )}
            </div>

            <ReactFlow
              nodes={nodes}
              edges={flowEdges}
              onNodesChange={handleNodesChange}
              onEdgesChange={handleEdgesChange}
              onNodeDragStop={handleNodeDragStop}
              onBeforeDelete={handleBeforeDelete}
              onConnect={onConnect}
              onNodeDoubleClick={handleNodeDoubleClick}
              onSelectionChange={onSelectionChange}
              onEdgeClick={onEdgeClick}
              onPaneClick={onPaneClick}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={GRAPH_FIT_VIEW_OPTIONS}
              deleteKeyCode={runnerActive || batchPending ? null : ['Backspace', 'Delete']}
              nodesConnectable={!runnerActive && !batchPending}
              nodesDraggable={!batchPending}
              edgesFocusable={!runnerActive}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#2d2d3d" gap={20} size={1} />
              <Controls fitViewOptions={GRAPH_FIT_VIEW_OPTIONS} />
              <MiniMap
                nodeColor={() => '#b87333'}
                maskColor="rgba(10, 10, 15, 0.7)"
              />
            </ReactFlow>

            {/* Preview pane — floating overlay */}
            <StoryPreviewPane
              story={story}
              selectedNodeId={selectedNodeId}
              orientation={previewOrientation}
              onOrientationChange={setPreviewOrientation}
              runnerActive={runnerActive}
              onRunnerActiveChange={setRunnerActive}
              poppedOut={poppedOut}
              onPopOut={popOut}
              onPopBack={popBack}
            />

            {/* Inline notice (refused connection, failed link) */}
            {notice && (
              <div className={styles.graphNotice} role="status" data-testid="graph-notice">
                {notice}
              </div>
            )}

            {/* Choice picker overlay */}
            {pendingConnection && (
              <div className={styles.choicePickerOverlay}>
                <div className={styles.choicePickerPanel} data-testid="choice-picker">
                  {pendingConnection.choices.length > 0 ? (
                    <>
                      <div className={styles.choicePickerTitle}>Which choice leads here?</div>
                      <div className={styles.choicePickerList}>
                        {pendingConnection.choices.map((c) => (
                          <button
                            key={c.id}
                            className={styles.choicePickerOption}
                            onClick={() => handleChoicePick(c.id)}
                          >
                            {c.text || '(no label)'}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className={styles.choicePickerTitle}>
                      Every choice of this scene already has an arrow.
                    </div>
                  )}
                  <button className={styles.choicePickerCancel} onClick={cancelChoicePick}>
                    {pendingConnection.choices.length > 0 ? 'Cancel' : 'Close'}
                  </button>
                </div>
              </div>
            )}

            {/* Edge properties panel */}
            {selectedEdge && !runnerActive && (() => {
              const sourceNode = story.graph.nodes.find((n) => n.id === selectedEdge.from);
              const targetNode = story.graph.nodes.find((n) => n.id === selectedEdge.to);
              const edgeChoices = sourceNode ? sceneChoicesMap.get(sourceNode.storySceneId) ?? [] : [];
              const siblings = story.graph.edges.filter((e) => e.from === selectedEdge.from && e.id !== selectedEdge.id);
              const currentCondition = selectedEdge.condition ?? '';
              const isDangling = danglingEdgeIds.has(selectedEdge.id);
              const conditionKnown = !currentCondition || edgeChoices.some((c) => c.id === currentCondition);
              const noneDisabled = edgeChoices.length > 0 || siblings.some((e) => !e.condition);
              return (
                <div className={styles.edgePropPanel} data-testid="edge-panel">
                  <div className={styles.edgePropHeader}>
                    <span className={styles.edgePropTitle}>Arrow</span>
                    <button className={styles.edgePropClose} onClick={() => setSelectedEdgeId(null)} aria-label="Close arrow panel">✕</button>
                  </div>
                  {isDangling && (
                    <p className={styles.edgePropDangling} role="alert">
                      {!sourceNode || !targetNode
                        ? 'This arrow points to a scene that is no longer on the graph. Delete it.'
                        : 'The choice this arrow followed no longer exists on the scene. Pick another choice or delete the arrow.'}
                    </p>
                  )}
                  <label className={styles.edgePropLabel} htmlFor="edge-condition">Condition</label>
                  <select
                    id="edge-condition"
                    className={styles.edgePropSelect}
                    value={currentCondition}
                    onChange={(e) => handleEdgeConditionChange(e.target.value)}
                  >
                    <option value="" disabled={noneDisabled && !!currentCondition}>None (next scene)</option>
                    {!conditionKnown && (
                      <option value={currentCondition} disabled>(missing choice)</option>
                    )}
                    {edgeChoices.map((c) => (
                      <option
                        key={c.id}
                        value={c.id}
                        disabled={siblings.some((e) => e.condition === c.id)}
                      >
                        {c.text || '(no label)'}
                      </option>
                    ))}
                  </select>
                  {selectedEdge.condition && (
                    <>
                      <label className={styles.edgePropLabel} htmlFor="edge-label">Label</label>
                      <input
                        id="edge-label"
                        className={styles.edgePropSelect}
                        type="text"
                        readOnly
                        value={selectedEdge.label ?? ''}
                        placeholder="(no label)"
                      />
                    </>
                  )}
                  <button className={styles.edgePropRemove} onClick={() => removeEdge(selectedEdge.id)}>
                    Delete arrow
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className={styles.sceneTabContent}>
          <SceneEditor
            key={activeTab}
            sceneIdProp={activeTab}
            onClose={() => closeSceneTab(activeTab)}
            isEmbedded={true}
          />
        </div>
      )}
      </>
      )}

      {/* Remove a scene from the story */}
      {publishStatus && (
        <ShareDialog
          show={shareOpen}
          onClose={() => setShareOpen(false)}
          storyName={story?.name ?? 'Story'}
          playerUrl={publishStatus.playerUrl}
          capabilityUrl={publishStatus.capabilityUrl}
          orientation={previewOrientation}
          version={publishStatus.version}
        />
      )}

      <ConfirmDeleteModal
        show={!!pendingRemoval}
        title={`Remove "${pendingRemoval?.sceneName || `Scene #${pendingRemoval?.sceneId ?? ''}`}" from this story?`}
        actions={[
          { key: 'remove', label: 'Remove', variant: 'danger', isDefault: true, run: confirmRemoveStoryScene },
        ]}
        onClose={() => setPendingRemoval(null)}
      >
        <p className="mb-1">Its node and every arrow to or from it are removed from the graph.</p>
        <p className="mb-0">
          {pendingRemoval?.mode === 'owned'
            ? 'The scene itself is kept: it moves to Home and appears in your scene list again.'
            : 'The scene itself is not changed.'}
        </p>
      </ConfirmDeleteModal>
    </div>
  );
};

export default StoryEditor;
